import React from 'react';

import { useQuery } from '@apollo/react-hooks';
// import Sentry SDK directly since this is published package
import * as Sentry from '@sentry/browser';
import { gql } from 'apollo-boost';

import { CompassTab } from '@atlassian/product-search-dialog';

// Mostly copied from src/sections/workspace/sections/settings/components/compass/use-compass-site-info.ts
const TENANT_CONTEXT_QUERY = gql`
  query compassInfo($cloudIds: [ID!]) {
    tenantContexts(cloudIds: $cloudIds) {
      cloudId
      hostName
    }
  }
`;

type TenantContext = {
  cloudId: string;
  hostName: string;
};

type TenantContextQueryResponse = {
  tenantContexts: TenantContext[];
};

type TenantContextQueryVariables = {
  cloudIds: string[];
};

/**
 * GraphQL call to get Tenant data
 * @param cloudId connected to the BBC workspace
 * @returns the Compass site URL that is connected
 */
export const useCompassSiteInfo = (
  cloudId?: string
): {
  siteUrl: string | null;
  loading: boolean;
  error?: Error | null;
} => {
  const { data, loading, error } = useQuery<
    TenantContextQueryResponse,
    TenantContextQueryVariables
  >(TENANT_CONTEXT_QUERY, {
    variables: {
      cloudIds: cloudId ? [cloudId] : [],
    },
    skip: !cloudId,
  });

  const tenantInfo = data?.tenantContexts?.[0];
  const siteUrl = tenantInfo ? `https://${tenantInfo.hostName}` : null;

  return {
    siteUrl,
    loading,
    error: error ? new Error(error.message) : null,
  };
};
// end of copied code

const HorizontalPlatformSearchCompassTab = ({
  cloudId,
  onRetry,
}: {
  cloudId: string;
  onRetry: () => void;
}) => {
  const { siteUrl, loading: isLoading, error } = useCompassSiteInfo(cloudId);

  if (error) {
    Sentry.captureException(error);
  }

  if (!siteUrl || isLoading || error) {
    return null;
  }

  return (
    <CompassTab
      baseUrl={siteUrl}
      crossFlowEnabled
      isCompassActivated
      onRetry={onRetry}
      order={1}
      sourceContext="bitbucket"
      // Manually providing `permissionSupplier` will render the tab for users that have a connected
      //  Compass instance but haven't joined it yet. The tab won't render results but will show a
      //  link to 'View all components' which users can click to request site access.
      permissionSupplier={() =>
        Promise.resolve(['compass.component-label', 'compass.component'])
      }
      userTeamConfig={{
        context: {
          contextType: 'filter',
          principalId: 'Context',
          productKey: 'compass',
          siteId: cloudId,
        },
        resolveTeamUrl: (teamId: string) =>
          `${siteUrl}/compass/people/team/${teamId}`,
        resolveUserUrl: (userId: string) => `${siteUrl}/people/${userId}`,
      }}
    />
  );
};

export default React.memo(HorizontalPlatformSearchCompassTab);
